<script setup lang="ts">
import { ref, computed } from 'vue'
import type { BoardColumn } from '@/types/landing'
import { useInView } from '@/composables/useInView'

interface Props {
  columns: BoardColumn[]
}

const props = defineProps<Props>()
const { isInView, targetRef } = useInView()
// 在模板中通过 ref="targetRef" 使用
void targetRef.value

const priorityColors: Record<string, string> = {
  high: '#F53F3F',
  medium: '#FF8F1F',
  low: '#00B578',
}

const priorityLabels: Record<string, string> = {
  high: '高',
  medium: '中',
  low: '低',
}

const isMobile = ref(false)
if (typeof window !== 'undefined') {
  const mql = window.matchMedia('(max-width: 767px)')
  isMobile.value = mql.matches
  mql.addEventListener('change', (e) => { isMobile.value = e.matches })
}

const columnsWithAnimation = computed(() =>
  props.columns.map((col, index) => ({
    ...col,
    _style: {
      transitionDelay: `${index * 100}ms`,
      opacity: isInView.value ? 1 : 0,
      transform: isInView.value ? 'translateY(0)' : 'translateY(20px)',
    },
  })),
)
</script>

<template>
  <section id="board" ref="targetRef" class="bg-[#F7F8FA] py-20 md:py-24">
    <div class="mx-auto max-w-[1200px] px-6">
      <!-- 标题 -->
      <div class="mb-10 text-center">
        <h2 class="mb-3 text-2xl font-semibold text-[#1D2129] md:text-3xl">
          每个任务从创建到归档，全程可追溯、可提醒、可统计
        </h2>
        <p class="mx-auto max-w-[560px] text-base text-[#86909C]">
          看板视图让你对工作进展一目了然
        </p>
      </div>

      <!-- 模拟浏览器外壳 -->
      <div class="mx-auto max-w-[1000px] overflow-hidden rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
        <!-- 浏览器标题栏 -->
        <div class="flex items-center gap-2 bg-[#F2F3F5] px-4 py-3">
          <div class="flex gap-1.5">
            <span class="h-3 w-3 rounded-full" style="background-color: #FF605C" />
            <span class="h-3 w-3 rounded-full" style="background-color: #FFBD44" />
            <span class="h-3 w-3 rounded-full" style="background-color: #00CA4E" />
          </div>
          <!-- 地址栏 -->
          <div class="mx-auto flex max-w-[400px] flex-1 items-center justify-center rounded-md bg-white px-4 py-1.5 text-xs text-[#86909C] shadow-sm">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            zhitaizhang.com/dashboard
          </div>
          <div class="w-16" />
        </div>

        <!-- 看板容器 -->
        <div
          class="bg-white p-4 md:p-6"
          :class="{ 'overflow-x-auto': isMobile }"
        >
          <div
            class="flex gap-4"
            :class="isMobile ? 'min-w-max' : ''"
          >
            <div
              v-for="column in columnsWithAnimation"
              :key="column.id"
              class="flex min-w-[200px] flex-1 flex-col rounded-lg bg-[#F7F8FA] p-3 transition-all duration-500 ease-out"
              :style="column._style"
            >
              <!-- 列标题 -->
              <div class="mb-3 flex items-center justify-between">
                <h3 class="text-sm font-semibold text-[#1D2129]">{{ column.title }}</h3>
                <span class="rounded-full bg-[#E5E6EB] px-2 py-0.5 text-xs text-[#86909C]">
                  {{ column.tasks.length }}
                </span>
              </div>

              <!-- 任务卡片 -->
              <div class="flex flex-col gap-2">
                <div
                  v-for="task in column.tasks"
                  :key="task.id"
                  class="relative cursor-pointer rounded-lg bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                >
                  <!-- 任务名 -->
                  <p class="mb-2 text-sm font-medium text-[#1D2129]">{{ task.title }}</p>

                  <!-- 优先级 + 状态 -->
                  <div class="flex items-center justify-between">
                    <span
                      class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium"
                      :style="{
                        backgroundColor: priorityColors[task.priority] + '15',
                        color: priorityColors[task.priority],
                      }"
                    >
                      {{ priorityLabels[task.priority] }}优先级
                    </span>
                    <span
                      class="text-xs"
                      :class="task.status === '已逾期' ? 'text-[#F53F3F]' : 'text-[#86909C]'"
                    >
                      {{ task.status }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
